import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Observable, of, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { SalesOrderDocumentsService } from '@shared/services/api/sales-order-documents.service';
import { SalesOrderDocumentsFiltersService } from '@shared/services/api/sales-order-documents-filters.service';
import { SalesOrderDocuments } from '@shared/entities/salesOrderDocuments';
import { SalesOrderDocumentsFilter } from '@shared/entities/salesOrderDocumentsFilter';
import { FilterSelectedEventType } from '@shared/models/types';
import { TabItem } from '@shared/components/icam-tabs/icam-tabs.component';
import { ToastService } from '@shared/services/toast.service';

@Component({
  selector: 'app-sales-order-documents',
  templateUrl: './sales-order-documents.component.html',
  styleUrl: './sales-order-documents.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SalesOrderDocumentsComponent implements OnInit {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _cdr = inject(ChangeDetectorRef);
  private _salesOrderDocumentsService = inject(SalesOrderDocumentsService);
  private _salesOrderDocumentsFiltersService = inject(SalesOrderDocumentsFiltersService);
  private _activatedRoute = inject(ActivatedRoute);
  private _location = inject(Location);
  private _translateService = inject(TranslateService);
  private _toastService = inject(ToastService);

  showSuccess = false;
  showError = false;

  allSalesOrderDocuments$: Observable<SalesOrderDocuments[]>;
  salesOrderDocumentsFilters$: Observable<SalesOrderDocumentsFilter>;

  salesOrderDocPrevPage?: number = undefined;
  salesOrderDocNextPage?: number = undefined;
  salesOrderDocCurrentPage = 1;
  salesOrderDocTotalPages?: number = undefined;

  salesOrderId: string | null = null;

  tabs: TabItem[] = [];
  selectedTabId = 'all';
  currentSerialNumberFilter: string | null = null;

  constructor() {
    this.allSalesOrderDocuments$ = this._salesOrderDocumentsService.allSalesOrderDocuments$.pipe(
      switchMap((paginatedResult) => {
        this.salesOrderDocPrevPage = paginatedResult.prevPage;
        this.salesOrderDocCurrentPage = paginatedResult.currentPage ?? 1;
        this.salesOrderDocNextPage = paginatedResult.nextPage;
        this.salesOrderDocTotalPages = paginatedResult.totalPage;
        this._cdr.markForCheck();
        return of(paginatedResult.data);
      })
    );

    this.salesOrderDocumentsFilters$ = this._salesOrderDocumentsFiltersService.all$;
  }

  ngOnInit(): void {
    this._activatedRoute.params
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((params) => {
          this.salesOrderId = params['id'];
          if (this.salesOrderId) {
            this.loadSalesOrderDocuments();
            this.loadFiltersAndTabs();
            this._cdr.markForCheck();
          }
        })
      )
      .subscribe();
  }

  private loadSalesOrderDocuments(): void {
    if (!this.salesOrderId) return;

    const filters: any = { salesOrderId: this.salesOrderId };

    if (this.currentSerialNumberFilter) {
      filters.serialNumber = this.currentSerialNumberFilter;
    }

    this._salesOrderDocumentsService.filters = filters;
  }

  private loadFiltersAndTabs(): void {
    if (!this.salesOrderId) return;
    this._salesOrderDocumentsFiltersService.filters = { salesOrderId: this.salesOrderId };

    this.salesOrderDocumentsFilters$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((filters) => {
          this.createTabsFromSerialNumbers(filters.serialNumbers);
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  private createTabsFromSerialNumbers(serialNumbers: string[]): void {
    this.tabs = [
      {
        id: 'all',
        label: 'SALES_ORDER_DOCUMENTS.TAB_ALL_DOCUMENTS',
        icon: 'article',
        needsTranslation: true
      }
    ];

    serialNumbers.forEach((serialNumber) => {
      this.tabs.push({
        id: serialNumber.split('-')[0].trim(),
        label: serialNumber,
        icon: 'vertical_shades',
        needsTranslation: false
      });
    });
  }

  onTabChange(tab: TabItem): void {
    this.selectedTabId = tab.id as string;
    this.currentSerialNumberFilter = tab.id === 'all' ? null : (tab.id as string);

    this.loadSalesOrderDocuments();
  }

  goBack(): void {
    this._location.back();
  }

  onPageSelected(page: number): void {
    this._salesOrderDocumentsService.refresh(page).pipe(takeUntilDestroyed(this._destroyRef)).subscribe();
  }

  onFilterSelected(filters: FilterSelectedEventType[]): void {
    console.log('Filters selected:', filters);
  }

  onDeleteDocument(row: SalesOrderDocuments): void {
    if (!row?.id) return;

    this._salesOrderDocumentsService
      .deleteSalesOrderDocument(row.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._salesOrderDocumentsService.refresh(this.salesOrderDocCurrentPage).subscribe();
          this._toastService.success(this._translateService.instant('SALES_ORDER_DOCUMENTS.DELETE_SUCCESS'));
          this._cdr.markForCheck();
        },
        error: (err) => {
          const message = err?.error?.errorMessage || this._translateService.instant('SALES_ORDER_DOCUMENTS.DELETE_ERROR');
          this._toastService.error(message);
          this._cdr.markForCheck();
        }
      });
  }

  getYear() {
    return new Date().getFullYear();
  }
}
